import { Close, CloseFullscreen, OpenInFull, PrintOutlined } from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    GlobalStyles,
    IconButton,
    Stack,
    ThemeProvider,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useState } from 'react';

import {
    buildDocumentContentCss,
    getDocumentCanvasStyle,
    normalizeFontFamilyStyles,
} from './documentContentStyles';
import { PAGE_SIZES } from 'config/defaultBranding';
import useTranslate from 'hooks/useTranslate';
import appThemeV2 from 'theme/themeV2';
import type { BrandingSettings, LogoSettings } from 'types';

const logoMaxHeight = (size: string | undefined, footer: boolean) => {
    if (footer) {
        if (size === 'small') return '40px';
        if (size === 'medium') return '60px';
        return '80px';
    }
    if (size === 'small') return '60px';
    if (size === 'medium') return '100px';
    return '140px';
};

const escapeAttr = (value: string) =>
    value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

const imageHtml = (src: string, maxHeight: string | number, position: string) => {
    const margin =
        position === 'center'
            ? 'margin-left:auto;margin-right:auto;'
            : position === 'right'
              ? 'margin-left:auto;'
              : '';
    return `<div style="text-align:${position};margin:12px 0"><img src="${escapeAttr(src)}" alt="" style="max-height:${maxHeight}${typeof maxHeight === 'number' ? 'px' : ''};max-width:100%;height:auto;display:block;${margin}" /></div>`;
};

const brandingImagesHtml = (
    branding: BrandingSettings | undefined,
    placement: 'top' | 'bottom'
) => {
    if (!branding) return '';
    if (placement === 'top') {
        return [
            branding.headerLogo
                ? imageHtml(
                      branding.headerLogo.url,
                      logoMaxHeight(branding.headerLogo.size, false),
                      branding.headerLogo.position
                  )
                : '',
            branding.header?.enabled && branding.header.image
                ? imageHtml(
                      branding.header.image.url,
                      branding.header.image.maxHeight,
                      branding.header.image.position
                  )
                : '',
        ].join('');
    }
    return [
        branding.footerLogo
            ? imageHtml(
                  branding.footerLogo.url,
                  logoMaxHeight(branding.footerLogo.size, true),
                  branding.footerLogo.position
              )
            : '',
        branding.footer?.enabled && branding.footer.image
            ? imageHtml(
                  branding.footer.image.url,
                  branding.footer.image.maxHeight,
                  branding.footer.image.position
              )
            : '',
    ].join('');
};

const PAGE_SIZE_CSS: Record<string, string> = {
    letter: '8.5in 11in',
    a4: 'A4',
    legal: '8.5in 14in',
};

const BrandingImage = ({
    src,
    maxHeight,
    position,
}: {
    src: string;
    maxHeight: string | number;
    position: LogoSettings['position'];
}) => (
    <Box sx={{ textAlign: position, my: 1.5 }}>
        <Box
            component="img"
            src={src}
            alt=""
            sx={{
                maxHeight,
                maxWidth: '100%',
                height: 'auto',
                display: 'block',
                ...(position === 'center' ? { mx: 'auto' } : null),
                ...(position === 'right' ? { ml: 'auto' } : null),
            }}
        />
    </Box>
);

const BrandingImages = ({
    branding,
    placement,
}: {
    branding?: BrandingSettings;
    placement: 'top' | 'bottom';
}) => {
    if (!branding) return null;
    if (placement === 'top') {
        return (
            <>
                {branding.headerLogo ? (
                    <BrandingImage
                        src={branding.headerLogo.url}
                        maxHeight={logoMaxHeight(branding.headerLogo.size, false)}
                        position={branding.headerLogo.position}
                    />
                ) : null}
                {branding.header?.enabled && branding.header.image ? (
                    <BrandingImage
                        src={branding.header.image.url}
                        maxHeight={branding.header.image.maxHeight}
                        position={branding.header.image.position}
                    />
                ) : null}
            </>
        );
    }
    return (
        <>
            {branding.footerLogo ? (
                <BrandingImage
                    src={branding.footerLogo.url}
                    maxHeight={logoMaxHeight(branding.footerLogo.size, true)}
                    position={branding.footerLogo.position}
                />
            ) : null}
            {branding.footer?.enabled && branding.footer.image ? (
                <BrandingImage
                    src={branding.footer.image.url}
                    maxHeight={branding.footer.image.maxHeight}
                    position={branding.footer.image.position}
                />
            ) : null}
        </>
    );
};

interface PrintPreviewProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    content: string;
    branding?: BrandingSettings;
}

const PrintPreviewDialog = ({
    onOpenChange,
    content,
    branding,
}: Omit<PrintPreviewProps, 'open'>) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [expanded, setExpanded] = useState(false);
    const fullScreen = isMobile || expanded;

    const page = PAGE_SIZES[branding?.pageSize || 'letter'];
    const pageSize = PAGE_SIZE_CSS[branding?.pageSize || 'letter'];
    const canvas = getDocumentCanvasStyle(branding);
    const printDocumentCss = buildDocumentContentCss('body', branding);
    const marginTop = branding?.marginTop ? `${branding.marginTop}in` : '1in';
    const marginBottom = branding?.marginBottom ? `${branding.marginBottom}in` : '1in';
    const marginLeft = branding?.marginLeft ? `${branding.marginLeft}in` : '1in';
    const marginRight = branding?.marginRight ? `${branding.marginRight}in` : '1in';

    const handleClose = () => onOpenChange(false);
    const printableContent = normalizeFontFamilyStyles(content);

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) return;

        printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${translate('printPreview')}</title>
            <style>
              @page {
                size: ${pageSize};
                margin: ${marginTop} ${marginRight} ${marginBottom} ${marginLeft};
              }
              ${printDocumentCss}
              body {
                background: ${canvas.backgroundColor};
                margin: 0;
                padding: 0;
              }
              .page-break {
                display: block !important;
                height: 0 !important;
                min-height: 0 !important;
                max-height: 0 !important;
                margin: 0 !important;
                padding: 0 !important;
                border: none !important;
                background: transparent !important;
                break-after: page !important;
                page-break-after: always !important;
              }
              .page-break span,
              .page-break::before,
              .page-break::after {
                display: none !important;
              }
              ul, ol { margin: 0.5em 0; padding-left: 1.5em; list-style-position: outside; }
              ul { list-style-type: disc; }
              ul ul { list-style-type: circle; }
              ol { list-style-type: decimal; }
              ol ol { list-style-type: lower-alpha; }
              li { display: list-item; }
              li > p { margin: 0; }
              @media print {
                body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
              }
            </style>
          </head>
          <body>${brandingImagesHtml(branding, 'top')}${printableContent}${brandingImagesHtml(branding, 'bottom')}</body>
        </html>
      `);
        printWindow.document.close();
        printWindow.focus();
        window.setTimeout(() => {
            printWindow.print();
        }, 250);
    };

    return (
        <Dialog
            open
            onClose={handleClose}
            fullWidth
            maxWidth="xl"
            fullScreen={fullScreen}
            aria-labelledby="template-print-preview-title"
            slotProps={{
                paper: {
                    sx: {
                        height: fullScreen ? '100%' : '88vh',
                        maxHeight: fullScreen ? '100%' : '88vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        borderRadius: fullScreen ? 0 : 2,
                    },
                },
            }}
        >
            <GlobalStyles styles={buildDocumentContentCss('.template-print-preview', branding)} />
            <GlobalStyles
                styles={{
                    '.template-print-preview .page-break': {
                        display: 'block',
                        height: '48px',
                        margin: '4px 0',
                        backgroundColor: '#e5e7eb',
                        boxShadow: '9999px 0 0 #e5e7eb, -9999px 0 0 #e5e7eb',
                        position: 'relative',
                        overflow: 'hidden',
                    },
                    '.template-print-preview .page-break span': { display: 'none' },
                    '.template-print-preview .page-break::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '1px',
                        background: '#c0c0c0',
                    },
                    '.template-print-preview .page-break::after': {
                        content: '"— PAGE BREAK —"',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        fontSize: '10px',
                        fontWeight: 600,
                        color: '#9ca3af',
                        letterSpacing: '1.5px',
                        background: '#e5e7eb',
                        padding: '0 10px',
                        whiteSpace: 'nowrap',
                    },
                }}
            />
            <DialogTitle
                component="div"
                id="template-print-preview-title"
                sx={{
                    px: { xs: 1.5, sm: 2.5 },
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                    flexShrink: 0,
                }}
            >
                <Stack
                    direction="row"
                    spacing={1.5}
                    sx={{ alignItems: 'center', justifyContent: 'space-between', minWidth: 0 }}
                >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                            variant="subtitle1"
                            component="h2"
                            noWrap
                            sx={{ fontWeight: 700, lineHeight: 1.3 }}
                        >
                            {translate('printPreview')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {translate('printPreviewDescription')}
                        </Typography>
                    </Box>
                    <Stack
                        direction="row"
                        spacing={0.75}
                        sx={{ alignItems: 'center', flexShrink: 0 }}
                    >
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<PrintOutlined />}
                            onClick={handlePrint}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            {translate('print')}
                        </Button>
                        {isMobile ? null : (
                            <Tooltip
                                title={
                                    expanded
                                        ? translate('exitFullScreen')
                                        : translate('viewFullScreen')
                                }
                            >
                                <IconButton
                                    size="small"
                                    onClick={() => setExpanded((value) => !value)}
                                    aria-label={
                                        expanded
                                            ? translate('exitFullScreen')
                                            : translate('viewFullScreen')
                                    }
                                >
                                    {expanded ? (
                                        <CloseFullscreen fontSize="small" />
                                    ) : (
                                        <OpenInFull fontSize="small" />
                                    )}
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title={translate('close')}>
                            <IconButton
                                size="small"
                                onClick={handleClose}
                                aria-label={translate('close')}
                            >
                                <Close fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
            </DialogTitle>
            <DialogContent
                sx={{
                    p: 0,
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    backgroundColor: 'grey.50',
                }}
            >
                <Box sx={{ p: { xs: 1.5, md: 3 } }}>
                    <Box
                        className="template-print-preview"
                        sx={{
                            width: '100%',
                            maxWidth: page.width,
                            minHeight: { xs: 'auto', md: page.height },
                            mx: 'auto',
                            boxShadow: 1,
                            borderRadius: 1,
                            bgcolor: canvas.backgroundColor,
                            color: canvas.color,
                            fontFamily: canvas.fontFamily,
                            fontSize: canvas.fontSize,
                            lineHeight: canvas.lineHeight,
                            pt: { xs: 2, md: marginTop },
                            pr: { xs: 2, md: marginRight },
                            pb: { xs: 3, md: marginBottom },
                            pl: { xs: 2, md: marginLeft },
                        }}
                    >
                        <BrandingImages branding={branding} placement="top" />
                        <Box dangerouslySetInnerHTML={{ __html: printableContent }} />
                        <BrandingImages branding={branding} placement="bottom" />
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export function PrintPreview({ open, onOpenChange, content, branding }: PrintPreviewProps) {
    if (!open) return null;

    return (
        <ThemeProvider theme={appThemeV2}>
            <PrintPreviewDialog onOpenChange={onOpenChange} content={content} branding={branding} />
        </ThemeProvider>
    );
}
